import ViewerEvents from "events/ViewerEvents";
import ApiUtils from "api/Utils";
import { getCachedData, getTempEntities, setTempEntities } from "api/Cache";
import { updateData } from "services/CacheService";
import * as Utils from "services/UtilsService";
import { getEntitiesId, getEntityAttributesList, getEntityId } from "actions/GlobalActions";
import { LayersActions, ViewerActions, FoldersActions } from "actions";
import { defaultViewProgram } from "constants/viewer";
import { Errors } from "constants/ui";
import CrossSectionBounds from "events/CrossSectionBounds";
import EventsEmitter from "events/EventsEmitter";
import { config } from "config";

export function handleLayers(layers: any) {
    if (ViewerEvents && layers !== null) {
        ViewerEvents.layers = layers;
    }
}

export function toRESTBool(value: boolean) {
    return value ? "1" : "0";
}

export function fromRESTBool(value: string) {
    return value === "1";
}

export function handleLayerEvent(eventId: string, data: any, layer: any) {
    var viewer: any = ViewerEvents.getViewer();
    if (!viewer) {
        console.error("viewer is not defined");
        return;
    }
    console.log("Viewer event for", eventId, layer !== null ? layer.name : "NULL", data, layer);
    if (eventId === "toggleVisibility") {
        const visible = parseInt(layer.layer_visible) === 1;
        //console.log("layer:", layer, "|visible:", visible);
        viewer.setVisibility(layer.ID, visible);
    } else if (eventId === "refreshView") {
        viewer.refreshView(ViewerEvents.layers, (layerId: string) => {
            return ViewerEvents.findElement(layerId);
        });
    } else if (eventId === "setPointSize") {
        const pointSize: number = data; //parseFloat(layer.layer_point_size);
        viewer.setPointSize(layer.ID, pointSize);
    } else if (eventId === "setDensitySettings") {
        const densitySettings = {
            densityThreshold: 0.1,
            densityColorMinValue: { r: 0, g: 0, b: 0 },
            densityColorMaxValue: { r: 1, g: 1, b: 1 },
            densityColorMinScale: 0,
            densityColorMaxScale: 0.5,
            densityAlphaMultiplier: 1,
            densityCutDepth: 0,
            densityRayMarchSteps: 8,
            densityEnableRayMarch: false,
            densityEnableLighting: false,
        };
        viewer.setDensitySettings(layer.ID, densitySettings);
    } else if (eventId === "setTransparency") {
        const value = parseFloat(layer.layer_opacity);
        viewer.setTransparency(layer.ID, value);
    } else if (eventId === "setDiffuseTransparency") {
        //To verify layer input
        const diffuseOpacity: number = data; //parseFloat(layer.layer_diffuse_opacity);
        viewer.setDiffuseTransparency(layer.ID, diffuseOpacity);
    } else if (eventId === "setDiffuseBlendMode") {
        //To verify layer input
        const diffuseBlendMode: number = parseInt(layer.layer_diffuse_blend_mode);
        viewer.setDiffuseBlendMode(layer.ID, diffuseBlendMode);
    } else if (eventId === "setBlockModelLimits") {
        //console.log('setBlockModelLimits.data:', data);
        const blockModelLimits: any = {
            indexFilterType: data.indexFilter, //0: none; 1:subset; 2:sliced
            regionType: data.regionType, //0:box
            worldAABBMin: [data.bounds["min"]["x"], data.bounds["min"]["y"], data.bounds["min"]["z"]],
            worldAABBMax: [data.bounds["max"]["x"], data.bounds["max"]["y"], data.bounds["max"]["z"]],
            enableBounds: [data.enable["x"], data.enable["y"], data.enable["z"]],
        };
        viewer.setBlockModelLimits(layer.ID, blockModelLimits);
    } else if (eventId === "setStaticColor") {
        const inv255 = 1.0 / 255;
        let colorLegend: any = {
            type: 1,
            dataDrivenColorAttribute: "", //Use first attribute name
            dataDrivenColorAttributeIndex: -1,
            dataDrivenColorMinValue: 0.0,
            dataDrivenColorMaxValue: 1.0,
            dataDrivenColorStepSize: 0.001,
            dataDrivenColorDistribution: [],
            dataDrivenUseAttributeGradient: false,
            dataDrivenAttributeGradient: [],
            staticColorDoubleSided: data.doubleSided,
            staticColorFrontSideRGB: [data.front.r * inv255, data.front.g * inv255, data.front.b * inv255],
            staticColorBackSideRGB: [data.back.r * inv255, data.back.g * inv255, data.back.b * inv255],
            gradientId: data.gradientId,
        };
        layer["color_legend_mode"] = colorLegend.type + "";
        viewer.setColorLegend(layer.ID, colorLegend);
        ViewerEvents.emit("updateColorLegend", layer);
        //viewerTools.setState({ needUpdate: true });
    } else if (eventId === "setDataDrivenColor") {
        const inv255 = 1.0 / 255;
        let colorLegend: any = {
            type: 2,
            statisticalMethodType: data.statisticalMethodType,
            statisticalIntervalType: data.statisticalIntervalType,
            dataDrivenColorAttribute: data.colorAttribute || "",
            dataDrivenColorAttributeIndex: parseInt(data.colorAttributeIndex),
            dataDrivenColorMinValue: parseFloat(data.rangeMin),
            dataDrivenColorMaxValue: parseFloat(data.rangeMax),
            dataDrivenColorStepSize: parseFloat(data.rangeStep),
            dataDrivenColorStepCount: parseFloat(data.rangeCount),
            dataDrivenColorDistribution: data.distribution,
            dataDrivenUseAttributeGradient: data.useAttributeGradient,
            dataDrivenAttributeGradient: data.attributeGradient,
            staticColorDoubleSided: data.doubleSided,
            staticColorFrontSideRGB: [data.front.r * inv255, data.front.g * inv255, data.front.b * inv255],
            staticColorBackSideRGB: [data.back.r * inv255, data.back.g * inv255, data.back.b * inv255],
            colorReverseGradient: data.colorReverseGradient,
            colorInterpolateContinuous: data.colorInterpolateContinuous,
            gradientId: data.gradientId,
        };
        console.log("colorLegend:", colorLegend);
        viewer.setColorLegend(layer.ID, colorLegend);
        ViewerEvents.emit("updateColorLegend", layer);
        //viewerTools.setState({ needUpdate: true });
    } else if (eventId === "setCollarLegend") {
        const type: number = parseInt(data.collarMode);
        const staticColorBytes: any = Utils.hexToRgb(data.collarColour);
        const staticRadius: number = parseFloat(data.collarSize);
        const textSize: number = parseFloat(data.collarTextSize);

        const inv255 = 1.0 / 255;

        const staticDriven = {
            type: type,
            staticColor: {
                r: staticColorBytes.r * inv255,
                g: staticColorBytes.g * inv255,
                b: staticColorBytes.b * inv255,
            },
            staticRadius: staticRadius,
            textSize: textSize,
        };

        layer["collar_legend_mode"] = data.collarMode;
        layer["collar_legend_color"] = data.collarColour;
        layer["collar_legend_radius"] = data.collarSize;

        viewer.setCollarLegend(layer.ID, staticDriven);
        //viewerTools.setState({needUpdate: true});
    } else if (eventId === "setTubeDiameterLegend") {
        const dataType: number = parseInt(data.scaleMode);

        let staticDriven = {
            type: dataType,
            staticRadius: data.cylinderRadius,
        };
        let dataDriven = {
            type: dataType,
            attributeName: data.attribute,
            attributeIndex: data.attributeIndex,
            minValue: data.rangeMin,
            maxValue: data.rangeMax,
            stepSize: data.rangeStep,
            minRadius: data.radiusMin,
            maxRadius: data.radiusMax,
            rawData:
                data.attribute +
                "," +
                data.rangeMin +
                "," +
                data.rangeMax +
                "," +
                data.rangeStep +
                "," +
                data.radiusMin +
                "," +
                data.radiusMax,
        };
        layer["tube_legend_scale_mode"] = dataType + "";
        layer["tube_legend_radius"] = parseFloat(data.cylinderRadius);
        layer["tube_legend_attribute"] = data.attribute;
        layer["tube_legend_range_min"] = parseFloat(data.rangeMin);
        layer["tube_legend_range_max"] = parseFloat(data.rangeMax);
        layer["tube_legend_range_step"] = parseFloat(data.rangeStep);
        layer["tube_legend_radius_min"] = parseFloat(data.radiusMin);
        layer["tube_legend_radius_max"] = parseFloat(data.radiusMax);

        viewer.setTubeDiameterLegend(layer.ID, dataType === 1 ? staticDriven : dataDriven);
        //viewerTools.setState({needUpdate: true});
    } else if (eventId === "setDrillholeSettings") {
        let drillholeSettings = {
            showRoots: data.showCollars,
            showRootLabels: data.showCollarLabels,
            showLines: !data.showCylinders,
            showTubes: data.showCylinders,
            showColorAttributeLabels: data.showAttributes,
            attributesTextSize: data.attributesTextSize,
            leftColorAttributeIndices: data.leftAttributeIndices,
            rightColorAttributeIndices: data.rightAttributeIndices,
            colorAttributeRawData: data.attributes,
        };
        layer["drillholes_show_collars"] = toRESTBool(data.showCollars);
        layer["drillholes_show_collar_labels"] = toRESTBool(data.showCollarLabels);
        layer["drillholes_show_cylinders"] = toRESTBool(data.showCylinders);
        layer["drillholes_show_attributes"] = toRESTBool(data.showAttributes);
        layer["drillholes_attributes"] = data.attributes;
        viewer.setDrillholeSettings(layer.ID, drillholeSettings);
        ViewerEvents.emit("updateLayer", layer);
    } else if (eventId === "setGeoChemSettings") {
        console.log("%s: %o", eventId, data);
        viewer.setGeoChemSettings(layer.ID, data);
        ViewerEvents.emit("updateLayer", layer);
    } else if (eventId === "setAttributeQueryBuilder") {
        let attributeQuery = {
            type: 1,
            builderColorAttributeFilters: data.builderAttributeFilters,
            builderColorAttributeRawData: data.builderAttributeRawData,
        };
        layer["attribute_query_mode"] = attributeQuery.type + "";
        layer["attribute_query_builder"] = data.attributeQueryBuilderValue;
        viewer.setAttributeQuery(layer.ID, attributeQuery);
        ViewerEvents.emit("updateLayer", layer);
    } else if (eventId === "setAttributeQueryString") {
        let attributeQuery = {
            type: 2,
            textColorAttributeRawData: data.textAttributeRawData,
        };
        layer["attribute_query_mode"] = attributeQuery.type + "";
        layer["attribute_query_string"] = data.attributeQueryString;
        viewer.setAttributeQuery(layer.ID, attributeQuery);
        ViewerEvents.emit("updateLayer", layer);
    } else if (eventId === "setLabelQueryBuilder") {
        let labelQuery = {
            type: 1,
            builderLabelFilters: data.builderLabelFilters,
            builderLabelRawData: data.builderLabelRawData,
        };
        layer["label_query_mode"] = labelQuery.type + "";
        layer["label_query_builder"] = data.labelQueryBuilderValue;
        viewer.setLabelQuery(layer.ID, labelQuery);
        //viewerTools.setState({needUpdate: true});
    } else if (eventId === "setLabelQueryString") {
        let labelQuery = {
            type: 2,
            textLabelRawData: data.textLabelRawData,
        };
        layer["label_query_mode"] = labelQuery.type + "";
        layer["label_query_string"] = data.labelQueryString;
        viewer.setLabelQuery(layer.ID, labelQuery);
        //viewerTools.setState({needUpdate: true});
    } else if (eventId === "setMeshPropertiesAt") {
        console.log("To complete mesh properties at handling");
        viewer.setMeshPropertiesAt(
            layer.ID,
            data.index,
            data.visible,
            data.transparency,
            data.useParent,
            data.staticColorFront,
            data.staticColorBack
        );
        //layer to complete ?
    } else if (eventId === "setMeshProperties") {
        console.log("To complete mesh properties handling");
        const meshProperties = data.meshProperties;
        viewer.setMeshProperties(layer.ID, meshProperties);
        //layer[..] to complete
    } else if (eventId === "setImagePropertiesAt") {
        //console.log("To complete image properties at handling:", data);
        viewer.setImagePropertiesAt(
            layer.ID,
            data.image.ID,
            data.visible,
            data.transparency,
            data.grayscale,
            data.contrast,
            data.brightness,
            data.gamma,
            data.blendMode, //1,2,3 or 4
            data.blendMakeColorTransparent, //boolean
            data.blendTransparentColor, //array of 3 floats
            data.blendSelectionTolerance, //float between 0 and 1
            data.blendOutputColor, //array of 4 floats
            data.blendInvertSelection, //boolean
            data.bands
        );
        //layer to complete ??
    } else if (eventId === "setImageProperties") {
        console.log("To complete image properties handling");
        const imageProperties = data.imageProperties;
        viewer.setImageProperties(layer.ID, imageProperties);
        //layer[..] to complete
    } else if (eventId === "deleteLayer") {
        deleteObject(layer.ID);
    } else if (eventId === "frameWorldBounds") {
        var { min, max } = data;
        viewer.frameWorldBounds([
            [parseFloat(min.x), parseFloat(min.y), parseFloat(min.z)],
            [parseFloat(max.x), parseFloat(max.y), parseFloat(max.z)],
        ]);
    }

    if (layer != null && !layer.not_selectable) {
        ViewerEvents.layers[layer.ID] = layer;
    }
}

export function addToCurrentProject(object: any) {
    var { currentProject } = ViewerEvents;
    var { viewer } = ViewerEvents;
    currentProject[object.ID] = object;
    //updateData("update", "entities", projectId, object);
    if (viewer) {
        viewer.addToCurrentProject(object);
    }
}

export function getGradientImage(gradientId: string) {
    if (ViewerEvents.viewer) {
        return ViewerEvents.viewer.getDefaultGradientImage(gradientId);
    }
}

export function getZoomToLayersIds(layers: any, inputs: any) {
    let attributeZoom: any = [],
        attributeZoomEntities: any = [];

    if (layers) {
        Object.values(layers).forEach((layer: any) => {
            if (inputs && inputs[layer.view_type]) {
                attributeZoom = Object.values(inputs[layer.view_type]).filter((elem: any) => elem.type === "3");
                attributeZoom.forEach((input: any) => {
                    let inputId: string = input.id;
                    attributeZoomEntities.push(layer["input_value_" + inputId]);
                });
            }

            if (layer["ortho_list"]) {
                var ortho_list = layer["ortho_list"].split(" ");
                ortho_list.forEach((ortho: string) => {
                    attributeZoomEntities.push(ortho);
                });
            }
        });
    }
    return attributeZoomEntities;
}
export function findItem(itemId: string) {
    const { projectId } = ViewerEvents;
    if (!projectId) {
        return;
    }
    const entities = getCachedData("entities");
    const project = entities[projectId];
    if (project) {
        return entities[projectId][itemId];
    }
    return;
}

export function findElement(projectId: string, itemId: string) {
    const cachedData = getCachedData();
    const entities = cachedData["entities"];
    const project = entities[projectId];
    if (project) {
        return entities[projectId][itemId];
    }
    return;
}

export function getAccessLevel(projectId: string) {
    let access_level = "read";
    const cachedData = getCachedData();
    const projectsRef = cachedData["projectsRef"];
    const found = projectsRef.find((ref: any) => ref["project_ref"] === projectId);
    if (found) {
        access_level = found["access_level"];
    }
    return access_level;
}

export function toggleVisibility(layerId: string, isVisible: boolean) {
    var layer = ViewerEvents.layers[layerId];
    if (layer) {
        layer["layer_visible"] = !isVisible ? "0" : "1";
        handleLayerEvent("toggleVisibility", null, layer);
    }
}

export function setTransparency(layerId: string, opacity: number) {
    var layer = ViewerEvents.layers[layerId];
    if (layer) {
        layer["layer_opacity"] = opacity + "";
        handleLayerEvent("setTransparency", null, layer);
    }
}

export function setMeshProperties(subLayer: any, meshProperty: any, update?: boolean) {
    if (update) {
        if (ViewerActions.compositeMeshes[subLayer.parent_layer]) {
            ViewerActions.compositeMeshes[subLayer.parent_layer]["subLayers"][meshProperty.meshIndex] = subLayer;
        }
        var modifiedValues: any = {
            index: subLayer.index,
            visible: subLayer.layer_visible,
            opacity: parseFloat(subLayer.layer_opacity) || 1.0,
            colorLegendMode: subLayer.color_legend_mode || "1",
            colors: [
                typeof subLayer.color_legend_static_r !== "undefined" ? subLayer.color_legend_static_r : 128,
                typeof subLayer.color_legend_static_g !== "undefined" ? subLayer.color_legend_static_g : 128,
                typeof subLayer.color_legend_static_b !== "undefined" ? subLayer.color_legend_static_b : 128,
            ],
            back_colors: [
                typeof subLayer.color_legend_static_back_r !== "undefined" ? subLayer.color_legend_static_back_r : 128,
                typeof subLayer.color_legend_static_back_g !== "undefined" ? subLayer.color_legend_static_back_g : 128,
                typeof subLayer.color_legend_static_back_b !== "undefined" ? subLayer.color_legend_static_back_b : 128,
            ],
        };

        Object.keys(ViewerEvents.pendingCompositeMeshes).forEach((layerID: string) => {
            ViewerEvents.pendingCompositeMeshes[layerID].forEach((submesh: any) => {
                if (submesh && submesh.index === subLayer.index) {
                    ViewerEvents.pendingCompositeMeshes[subLayer.parent_layer][subLayer.index + ""] = modifiedValues;
                } else {
                    if (!ViewerEvents.pendingCompositeMeshes[subLayer.parent_layer]) {
                        ViewerEvents.pendingCompositeMeshes[subLayer.parent_layer] = [];
                    }
                    ViewerEvents.pendingCompositeMeshes[subLayer.parent_layer][subLayer.index + ""] = modifiedValues;
                }
            });
        });
    }
    //console.log('pendingCompositeMeshes:',ViewerEvents.pendingCompositeMeshes);

    if (ViewerEvents.viewer) {
        ViewerEvents.viewer.setMeshPropertiesAt(
            meshProperty.parentLayer,
            meshProperty.meshIndex,
            meshProperty.meshVisible,
            meshProperty.meshOpacity,
            meshProperty.meshUseParentColorLegend,
            meshProperty.meshStaticColorFront,
            meshProperty.meshStaticColorBack
        );
    }
}

export function allowedSaveType(type: string) {
    var allowedType: string = "folder, raster";
    return allowedType.includes(type);
}

export function handleSliceMode(presetType: any) {
    const viewer: any = ViewerEvents.viewer;
    if (!viewer) {
        return;
    }
    switch (presetType) {
        case 0:
        default:
            break;
        case 1:
            viewer.alignCrossSectionXNormal(false);
            break;

        case 2:
            //Using flipped normal with this preset so that we align with the plane facing upwards
            viewer.alignCrossSectionZNormal(/*false*/ true);
            break;

        case 3:
            viewer.alignCrossSectionXNormal(false);
            break;

        case 4:
            viewer.alignCrossSectionYNormal(false);
            break;

        case 5:
            viewer.alignCrossSectionZNormal(false);
            break;

        case 6:
            viewer.alignCrossSectionXNormal(true);
            break;

        case 7:
            viewer.alignCrossSectionYNormal(true);
            break;

        case 8:
            viewer.alignCrossSectionZNormal(true);
            break;

        case 9:
            viewer.alignCrossSectionXNormal(false);
            break;

        case 10:
            viewer.alignCrossSectionYNormal(false);
            break;

        case 11:
            viewer.alignCrossSectionCustomNormal(false);
            break;
    }
}

export function getLayersAttributes(inputs: any, callback: any) {
    var { layers } = ViewerEvents;
    var entitiesAttributes: any = [];

    Object.values(layers).forEach((layer: any) => {
        entitiesAttributes[layer.ID] = getEntitiesId(layer, inputs, layer["view_type"], "3");
    });
    getEntityAttributesList(entitiesAttributes, (attributes: any) => {
        LayersActions.attributes = { ...LayersActions.attributes, ...attributes };
        console.log("%cLayersActions.attributes:", "color:yellow:", attributes);
        console.log("%cViewerActions.extendedMeta:", "color:yellow:", ViewerActions.extendedMeta);
        console.log("%cLayersActions.entitiesData:", "color:yellow:", LayersActions.entitiesData);
        EventsEmitter.emit("extendedMetaReady", true);
        callback();
    });
}

export function checkCompositeMesh(inputs: any) {
    var { layers, currentProject, view } = ViewerEvents;
    if (!view) {
        return;
    }
    var projectId = view.project;
    Object.values(layers).forEach((layer: any) => {
        if (inputs && inputs[layer.view_type]) {
            var attributeQuery: any = Object.values(inputs[layer.view_type]).find((elem: any) => elem.type === "3");
            if (attributeQuery) {
                let inputId: string = inputs[layer.view_type][attributeQuery.entity].id;
                let attributeQueryEntity = layer["input_value_" + inputId];
                if (currentProject[attributeQueryEntity] && currentProject[attributeQueryEntity]["mesh_count"]) {
                    var childrenCount = currentProject[attributeQueryEntity]["mesh_count"];
                    if (attributeQueryEntity && childrenCount) {
                        var mesh = {
                            ID: layer.ID,
                            mesh_count: childrenCount,
                            subLayers: [],
                        };
                        ApiUtils.fetchSetConfig(projectId, layer.ID).then((config: any) => {
                            //console.log('config:', config);
                            ApiUtils.fetchSetNames(projectId, attributeQueryEntity).then((data: any) => {
                                var subLayers: any = [];
                                var subLayer: any = {
                                    view_type: layer.view_type,
                                    input_value_e: layer.input_value_e,
                                    not_selectable: true,
                                    layer_visible: "1",
                                    layer_opacity: "1.0",
                                    parent_layer: layer.ID,
                                    use_parent: "1",
                                };
                                var names = data.trim().split("\n");
                                subLayer = inherentSpecificInputs(layer, subLayer);
                                names.forEach((name: string, index: number) => {
                                    var newLayer = { ...subLayer };
                                    newLayer["name"] = name;
                                    newLayer["ID"] = Utils.createGuid();
                                    newLayer["index"] = index;
                                    subLayers.push(newLayer);
                                });
                                if (config !== "") {
                                    config = JSON.parse(config);
                                    config.forEach((entry: any) => {
                                        var currentSubLayer = subLayers[entry.index];
                                        currentSubLayer["use_parent"] = entry.useParent;
                                        currentSubLayer["layer_visible"] = entry.visible;
                                        currentSubLayer["layer_opacity"] = entry.opacity;
                                        if (entry.colors) {
                                            currentSubLayer["color_legend_static_r"] = entry.colors[0];
                                            currentSubLayer["color_legend_static_g"] = entry.colors[1];
                                            currentSubLayer["color_legend_static_b"] = entry.colors[2];
                                            currentSubLayer["color_legend_static_back_r"] = entry.back_colors[0];
                                            currentSubLayer["color_legend_static_back_g"] = entry.back_colors[1];
                                            currentSubLayer["color_legend_static_back_b"] = entry.back_colors[2];
                                            currentSubLayer["color_legend_mode"] = entry.colorLegendMode;
                                        }

                                        var meshProperty = {
                                            parentLayer: layer.ID,
                                            meshIndex: entry.index,
                                            meshVisible: entry.visible === "1",
                                            meshOpacity: parseFloat(entry.opacity),
                                            meshUseParentColorLegend: false,
                                            meshStaticColorFront: entry.colors
                                                ? [
                                                      entry.colors[0] / 256.0,
                                                      entry.colors[1] / 256.0,
                                                      entry.colors[2] / 256.0,
                                                  ]
                                                : [],
                                            meshStaticColorBack: entry.back_colors
                                                ? [
                                                      entry.back_colors[0] / 256.0,
                                                      entry.back_colors[1] / 256.0,
                                                      entry.back_colors[2] / 256.0,
                                                  ]
                                                : [],
                                        };
                                        if (!ViewerEvents.pendingCompositeMeshes[subLayer.parent_layer]) {
                                            ViewerEvents.pendingCompositeMeshes[subLayer.parent_layer] = [];
                                        }
                                        ViewerEvents.pendingCompositeMeshes[subLayer.parent_layer][
                                            meshProperty.meshIndex.toString()
                                        ] = entry;
                                        ViewerEvents.viewer.setMeshPropertiesAt(
                                            meshProperty.parentLayer,
                                            meshProperty.meshIndex,
                                            meshProperty.meshVisible,
                                            meshProperty.meshOpacity,
                                            meshProperty.meshUseParentColorLegend,
                                            meshProperty.meshStaticColorFront,
                                            meshProperty.meshStaticColorBack
                                        );
                                    });
                                } else {
                                    ViewerEvents.pendingCompositeMeshes[mesh.ID] = mesh.subLayers;
                                }
                                mesh.subLayers = subLayers;
                                ViewerActions.compositeMeshes[mesh.ID] = mesh;
                            });
                        });
                    }
                }
            }
        }
    });
}

export function addTempEntity(props: {
    items: any;
    folderId: string;
    customPrograms?: any;
    loadProcessMeta?: boolean;
    metaCallback?: any;
    remote?: boolean;
    done?: any;
}) {
    const { items, folderId, metaCallback, customPrograms, loadProcessMeta, remote, done } = props;
    addObjects({
        items,
        folderId,
        customPrograms,
        loadProcessMeta,
        metaCallback,
        remote,
        done: (object: any) => {
            if (object !== null) {
                handleLayers(object);
            }
            setTempEntities([]);
            if (done) {
                done(object);
            }
        },
    });
}

export function loadTempEntities(metaCallback?: any, done?: any) {
    const tempEntities = getTempEntities();
    if (tempEntities.length > 0) {
        addObjects({
            items: tempEntities,
            folderId: "0",
            loadProcessMeta: true,
            metaCallback,
            done: (object: any) => {
                handleLayers(object);
                setTempEntities([]);
                if (done) {
                    done(object);
                }
            },
        });
    }
}

export function getMetaData(projectId: string, programId: string, values: any, callback: (metaData: any) => void) {
    const program = findElement(projectId, programId);
    if (!program) {
        callback(null);
        console.error("Can't find program:", programId);
    }
    const code = program.code;
    if (code) {
        ApiUtils.getViewProgramMetaFromCode(projectId, code, values, (viewmeta: any) => {
            if (viewmeta.result.status === "success") {
                let byteChars = atob(viewmeta.metadata);
                let bytes = new Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) {
                    bytes[i] = byteChars.charCodeAt(i);
                }
                var viewer = ViewerEvents.viewer;
                if (viewer) {
                    let viewMeta = viewer.createViewMetaData();
                    viewMeta.deserialize(bytes);
                    callback(viewMeta);
                } else {
                    callback(null);
                }
            } else {
                let error = viewmeta.result?.error_info;
                if (error) {
                    console.error("entity:", values, "has the following error:", atob(error));
                }
                //delete selectedViewContainerProperties[values["ID"]];
                callback(null);
                EventsEmitter.emit("showMessage", {
                    message: Errors.ERROR_ADD_OBJECT,
                });
            }
        });
    } else {
        console.error("can't find program:", programId);
        /*
        EventsEmitter.emit("showMessage", {
            message: Errors.ERROR_ADD_OBJECT,
        });
        */
    }
}

function readInputValues(inputs: any, entity: any) {
    //console.log("readInputValues.inputs:", inputs, "|entity:", entity);
    var values: any = { ...entity };
    for (let i in inputs) {
        var { id, type, label, filter } = inputs[i];
        if (type) {
            values["input_type_" + id] = type;
        }
        if (label) {
            values["input_label_" + id] = label;
        }
        if (filter) {
            values["input_filter_" + id] = filter;
        }
        if (type === "1") values["input_value_" + inputs[i].id] = new Date();
        else if (type === "6") {
            var value = inputs[i].default || "0";
            values["input_value_" + inputs[i].id] = value + "";
        }
    }
    //console.log('values:', values);
    return values;
}

export function deleteObject(id: string) {
    const { view, layers, viewer, viewMetaData, pendingDeletion, inputs } = ViewerEvents;

    pendingDeletion[id] = true;
    delete viewMetaData[id];
    delete layers[id];
    const entityContainer = view["entity_container"].split(" ");
    const index = entityContainer.findIndex((objId: string) => objId === id);
    entityContainer.splice(index, 1);
    view["entity_container"] = entityContainer.join(" ");
    var compositedMeta = viewer.createCompositeViewMeta(viewMetaData, Object.keys(viewMetaData));
    ViewerEvents.compositeMeta = compositedMeta;
    ViewerEvents.viewMetaData = viewMetaData;
    ViewerEvents.layers = layers;
    ViewerEvents.pendingDeletion = pendingDeletion;
    ViewerEvents.view = view;

    viewer.setViewMetaData(compositedMeta);
    ViewerEvents.emit("metadataChanged", {
        viewMetaData,
        layers,
        inputs,
    });
}

const filterInputs = (inputs: any, item: any, values: any) => {
    //console.log("filterInputs.inputs:", inputs, "|item:", item, "|values:", values);
    let newValues = { ...values };
    const viewer = ViewerEvents.viewer;
    if (viewer) {
        let input: any = Object.values(inputs).find(
            (input: any) => input.type === "3" && viewer.typeInFilter(item.file_type, input.filter)
        );

        if (input) {
            newValues["input_value_" + input.id] = item.ID;
        }
    }
    return newValues;
};

const addViewLayer = (programId: string, inputs: any, values: any, done: (id: string, newObject: any) => void) => {
    //console.log("addViewLayer.programId:", programId, "|inputs:", inputs);
    const selectedViewContainerProperties = ViewerEvents.layers || [];
    let newValues = { ...values };

    ApiUtils.serverCreateId((id: string) => {
        let newObject = {
            ...newValues,
            ID: id,
            view_type: programId,
        };
        //console.log("792.newObject:", newObject);
        newObject = readInputValues(inputs[programId], newObject);
        //console.log("794.newObject:", newObject);
        const currentProject = ViewerEvents.currentProject;

        ViewerActions.loadDefaultValues(newObject, currentProject, (layer: any, loadMetaData: boolean) => {
            //console.log("layer:", layer);
            selectedViewContainerProperties[id] = layer;
            if (done) {
                console.log("selectedViewContainerProperties:", selectedViewContainerProperties);
                addToCurrentProject(layer);
                ViewerActions.emit("updateLayers", selectedViewContainerProperties);
                EventsEmitter.emit("layersReady", {
                    layers: selectedViewContainerProperties,
                    inputs: ViewerEvents.inputs,
                });
                done(id, newObject);
            }
        });
    });
};

export function addObjectsWithInputs(layer: any, inputs: any, callback: any) {
    const selectedViewContainerProperties = ViewerEvents.layers || [];
    const selectedViewContainerMeta = ViewerEvents.viewMetaData || [];
    const viewer = ViewerEvents.viewer;
    const currentProject = ViewerEvents.currentProject;
    const program = layer["view_type"];
    const projectId = layer["project"];
    const type = layer["view_type"];

    let childrenMeta: any = selectedViewContainerMeta;

    const processLayer = (newObject: any) => {
        addViewLayer(program, inputs, newObject, (layerId: string, layer: any) => {
            const projectId = layer["project"];
            const type = layer["view_type"];
            getMetaData(projectId, type, layer, (viewMeta: any) => {
                if (viewMeta === null) {
                    if (callback) {
                        callback(selectedViewContainerProperties);
                    }
                    return;
                }
                childrenMeta[layerId] = viewMeta;
                var compositeMeta = viewer.createCompositeViewMeta(childrenMeta, Object.keys(childrenMeta));
                viewer.setViewMetaData(compositeMeta);
                ViewerEvents.viewMetaData = childrenMeta;
                ViewerActions.compositeMeta = compositeMeta;
                LayersActions.needsUpdate = true;
                CrossSectionBounds.initCrossSectionBounds(compositeMeta, currentProject);
                var currentInputs = ViewerEvents.inputs || {};
                Object.keys(inputs).forEach((id: string) => {
                    currentInputs[id] = inputs[id];
                });
                ViewerEvents.newScene = false;
                ViewerEvents.metaDataChanged = true;
                ViewerEvents.inputs = currentInputs;
                ViewerEvents.emit("metadataChanged", {
                    viewMetaData: childrenMeta,
                    layers: selectedViewContainerProperties,
                    inputs: ViewerEvents.inputs,
                });
                Object.values(selectedViewContainerProperties).forEach((layer: any) => {
                    updateData("update", "entities", projectId, layer);
                });
                //updateData("update", "pending", selectedView, layer);
                if (callback) {
                    callback(selectedViewContainerProperties);
                }
            });
        });
    };
    if (Boolean(config.api.filters === "true")) {
        ApiUtils.getRemoteObjects([type], projectId, () => {
            processLayer(layer);
        });
    } else {
        processLayer(layer);
    }
}

export function addObjects(props: {
    items: any;
    folderId: string;
    loadProcessMeta?: boolean;
    metaCallback?: any;
    customPrograms?: any;
    remote?: boolean;
    done: (object: any) => void;
}) {
    console.log("================>addObjects:", props);
    const { items, folderId, customPrograms, remote, loadProcessMeta, done } = props;
    let { metaCallback } = props;
    const selectedViewContainerProperties = ViewerEvents.layers || [];
    const selectedViewContainerMeta = ViewerEvents.viewMetaData || [];
    const viewer = ViewerEvents.viewer;
    const projectId = ViewerEvents.projectId;

    var selectedViewDeleted = ViewerEvents.pendingDeletion;
    Object.keys(selectedViewDeleted).forEach((id: string) => {
        if (selectedViewContainerMeta[id]) {
            delete selectedViewContainerMeta[id];
        }
        if (selectedViewContainerProperties[id]) {
            delete selectedViewContainerProperties[id];
        }
    });

    let childrenMeta: any = selectedViewContainerMeta;
    let pendingItems = items.length;
    let processedItems: any = [];
    let planeGeneratorItems: any = [];
    let planeGeneratorIds: any = [];
    let programs: any = [];
    let inputs: any = {};
    let totalPendingItems = pendingItems;

    const processMeta = (layer: any) => {
        const type = layer["view_type"];
        const layerId = layer["ID"];
        setTempEntities([]);
        getMetaData(projectId, type, layer, (viewMeta: any) => {
            totalPendingItems--;
            if (viewMeta === null) {
                if (done) {
                    done(selectedViewContainerProperties);
                }
                return;
            }
            childrenMeta[layerId] = viewMeta;

            if (totalPendingItems === 0) {
                viewer.processLayers(selectedViewContainerProperties, () => {
                    ViewerEvents.emit("metadataChanged", {
                        viewMetaData: childrenMeta,
                        layers: selectedViewContainerProperties,
                        inputs: ViewerEvents.inputs,
                    });
                    if (done) {
                        done(selectedViewContainerProperties);
                    }
                });
            }
        });
    };

    if (!metaCallback) {
        metaCallback = processMeta;
    }

    const processItem = async (program: string, values: any) => {
        addViewLayer(program, inputs, values, (layerId: string, layer: any) => {
            if (planeGeneratorItems.length > 0) {
                ViewerActions.compositeRasters[layerId] = planeGeneratorItems;
            }

            //console.log("layer:", layer);

            const type = layer["view_type"];
            if (type === defaultViewProgram.SURFCOMPOSITE) {
                values["input_value_timestamp"] = "999999999999";
            }
            if (Boolean(config.api.filters === "true")) {
                console.log("type:", type);
                ApiUtils.getEntity(type, projectId, () => {
                    metaCallback(layer, values);
                    if (loadProcessMeta) {
                        processMeta(layer);
                    }
                });
            } else {
                metaCallback(layer, values);
                if (loadProcessMeta) {
                    processMeta(layer);
                }
            }
        });
    };

    const handleItems = (items: any, success: any) => {
        items.forEach((itemId: string) => {
            let vfItem = findItem(itemId);
            if (vfItem) {
                if (customPrograms && customPrograms[itemId]) {
                    programs.push(customPrograms[itemId]);
                    processedItems.push(vfItem);
                    pendingItems--;
                    console.log("pendingItems:", pendingItems);
                    if (pendingItems === 0) {
                        success();
                    }
                } else {
                    const fileType = vfItem.file_type;
                    console.log("fileType:", fileType);
                    let program = defaultViewProgram[fileType];
                    if (typeof program === "undefined") {
                        done(null);
                    }
                    console.log("program:", program);
                    programs[program] = program;
                    if (program === defaultViewProgram.IDXORTHO) {
                        planeGeneratorItems.push(vfItem);
                        planeGeneratorIds.push(vfItem.ID);
                        pendingItems--;
                    } else {
                        processedItems.push(vfItem);
                        pendingItems--;
                    }
                    if (pendingItems === 0) {
                        success();
                    }
                }
            } else {
                done(null);
            }
        });
    };

    const processItems = (items: any, success: () => void) => {
        if (items.length === 0) {
            done(null);
            return;
        }
        if (remote) {
            ApiUtils.getRemoteObjects(items, projectId, () => {
                handleItems(items, success);
            });
        } else {
            handleItems(items, success);
        }
    };

    const processInputs = () => {
        console.log("inputs:", inputs);
        processedItems.forEach((item: any) => {
            let values: any = {};
            let program = defaultViewProgram[item.file_type];
            if (program === defaultViewProgram.IDXORTHO) {
                values["ortho_list"] = planeGeneratorIds.join(" ");
                let item = planeGeneratorItems[0];
                if (typeof item.name === "string") {
                    values["name"] = item.name;
                }
                if (folderId !== "0") values["view_folder"] = folderId;
                pendingItems -= planeGeneratorItems.length;
                pendingItems++;
                processItem(program, values);
            } else {
                if (customPrograms && customPrograms[item.ID]) {
                    program = customPrograms[item.ID];
                    if (typeof item.name === "string") {
                        values["name"] = item.name;
                        values["project"] = projectId;
                        values["view_type"] = customPrograms[item.ID];
                    }
                    if (folderId !== "0") values["view_folder"] = folderId;
                    values = filterInputs(inputs[program], item, values);
                    //console.log("1046:values:", values);
                    processItem(program, values);
                } else if (program === defaultViewProgram[item.file_type]) {
                    if (typeof item.name === "string") {
                        values["name"] = item.name;
                        values["project"] = projectId;
                    }
                    if (item["file_type"] === "SURFCOMPOSITE") {
                        values["input_value_timestamp"] = "999999999999";
                    }
                    if (folderId !== "0") values["view_folder"] = folderId;
                    values = filterInputs(inputs[program], item, values);
                    if (program === defaultViewProgram.GEOCHEM) {
                        //console.log("1059.values:", values);
                        processItem(program, values);
                    } else {
                        //console.log("1063.values:", values);
                        processItem(program, values);
                    }
                }
            }
        });
    };

    processItems(items, () => {
        let programsCount = Object.values(programs).length;
        programs = Utils.cleanArray(Object.values(programs));
        programs.forEach((program: string) => {
            ApiUtils.fetchProgramInputs(program, (response: any) => {
                if (response.result.status === "success") {
                    inputs[program] = response.inputs;
                    programsCount--;
                    if (programsCount === 0) {
                        ViewerEvents.inputs = inputs;
                        processInputs();
                    }
                } else {
                    if (response.result.error_info) {
                        console.error(`program ${program}`, atob(response.result.error_info));
                    }

                    if (done) {
                        EventsEmitter.emit("showMessage", {
                            message: Errors.ERROR_ADD_OBJECT,
                        });
                        done(null);
                    }
                }
            });
        });
    });
}

export function teleportToLayer(layer: any) {
    let clusterIndex = 0;
    const entityId = getEntityId(layer);
    const entity = findItem(entityId);
    const extendedMeta = ViewerActions.extendedMeta[entityId] || entity;
    if (extendedMeta) {
        const clusterCount: number = parseInt(extendedMeta["cluster_count"]) || 0;
        let minX = 0,
            minY = 0,
            minZ = 0,
            maxX = 0,
            maxY = 0,
            maxZ = 0;
        if (clusterCount > 0) {
            minX = parseFloat(extendedMeta["cluster" + clusterIndex + "_aabb_min_x"]);
            minY = parseFloat(extendedMeta["cluster" + clusterIndex + "_aabb_min_y"]);
            minZ = parseFloat(extendedMeta["cluster" + clusterIndex + "_aabb_min_z"]);
            maxX = parseFloat(extendedMeta["cluster" + clusterIndex + "_aabb_max_x"]);
            maxY = parseFloat(extendedMeta["cluster" + clusterIndex + "_aabb_max_y"]);
            maxZ = parseFloat(extendedMeta["cluster" + clusterIndex + "_aabb_max_z"]);
            clusterIndex++;
            if (clusterIndex >= clusterCount) {
                clusterIndex = 0;
            }
        } else {
            minX = parseFloat(extendedMeta["aabb_min_x"]);
            minY = parseFloat(extendedMeta["aabb_min_y"]);
            minZ = parseFloat(extendedMeta["aabb_min_z"]);
            maxX = parseFloat(extendedMeta["aabb_max_x"]);
            maxY = parseFloat(extendedMeta["aabb_max_y"]);
            maxZ = parseFloat(extendedMeta["aabb_max_z"]);
        }
        const coordinates = [(maxX + minX) / 2, (maxY + minY) / 2, (maxZ + minZ) / 2];
        const cameraDist = Math.max((maxX - minX) / 2, (maxY - minY) / 2, (maxZ - minZ) / 2);
        const viewer = ViewerActions.viewer;
        if (viewer) {
            viewer.teleportToGeo(coordinates, cameraDist);
        }
    }
}

function captureViewThumbnail(callback: any) {
    const viewer = ViewerEvents.viewer;

    if (viewer && viewer !== null) {
        viewer.captureFrame(function (canvas: any) {
            var smallCanvas: any = document.createElement("CANVAS");
            var dim = canvas.width;
            if (canvas.height < dim) dim = canvas.height;
            smallCanvas.width = dim;
            smallCanvas.height = dim;
            var destCanvasContext = smallCanvas.getContext("2d");
            destCanvasContext.drawImage(
                canvas,
                -(canvas.width - smallCanvas.width) / 2,
                -(canvas.height - smallCanvas.height) / 2
            );
            var imgData = smallCanvas.toDataURL("image/png");

            var img: any = document.createElement("IMG");
            img.width = dim;
            img.height = dim;
            img.onload = function () {
                var iconCanvas: any = document.createElement("CANVAS");
                iconCanvas.width = 256;
                iconCanvas.height = 256;
                var iconContext = iconCanvas.getContext("2d");
                iconContext.drawImage(img, 0, 0, iconCanvas.width, iconCanvas.height);
                var iconData = iconCanvas.toDataURL("image/jpeg");
                ApiUtils.serverCreateId((id) => {
                    callback(id, iconData);
                });
            };
            img.setAttribute("src", imgData);
        });
    }
}

function inherentSpecificInputs(layer: any, subLayer: any) {
    Object.entries(layer).forEach((property: any) => {
        switch (property[0]) {
            case "input_type_colorlegend":
            case "input_value_colorlegend":
            case "color_legend_mode":
            case "color_legend_static_r":
            case "color_legend_static_g":
            case "color_legend_static_b":
            case "color_legend_attribute":
            case "color_legend_attribute_index":
            case "color_legend_gradient":
            case "color_legend_range_max":
            case "color_legend_range_min":
            case "color_legend_range_step": {
                subLayer[property[0]] = property[1];
            }
        }
    });
    return subLayer;
}

function saveSlides() {
    var view = ViewerEvents.view;
    if (!view || !view.project) {
        return;
    }
    var projectId = view.project;
    var selectedView = view.ID;

    var selectedViewSnapshots = getCachedData("snapshots")[selectedView] || [];
    var slides = Object.values(selectedViewSnapshots).filter((snapshot: any) => snapshot.state === "pending");
    var processed = slides.length;
    if (processed === 0) {
        return;
    }
    slides.forEach((snapshot: any) => {
        var slide: any = {};
        slide["ID"] = snapshot.ID;
        slide["project"] = "slides:" + selectedView;
        slide["type"] = "SLIDE";
        slide["date"] = snapshot.date;
        slide["description"] = snapshot.description;
        slide["camera_origin"] = JSON.stringify(snapshot.cameraOrigin);
        slide["focus_origin"] = JSON.stringify(snapshot.focusOrigin);

        ApiUtils.remoteUpdateEntity(projectId, snapshot.ID, slide, function (response: any) {
            if (response.result === "success") {
                snapshot.state = "saved";
                var file = new File([snapshot.icon], "thumbnail.txt");
                ApiUtils.postFileToServer(
                    projectId,
                    snapshot.ID,
                    file,
                    0,
                    function () {
                        console.log("saving slide:", snapshot.ID);
                    },
                    function () {
                        // todo: show error
                    }
                );
                processed--;
                if (processed === 0) {
                    updateData("save", "snapshots", selectedView, selectedViewSnapshots);
                }
            }
        });
    });
}

function saveObjectRemotely(object: any, callback?: any) {
    if (!object || !object.ID || !object.project) {
        console.log("Can not save object remotely:", object);
        return;
    }
    const ID = object.ID;
    const projectId = object.project;
    ApiUtils.remoteUpdateEntity(projectId, ID, object, (response: any) => {
        if (response.result === "success") {
            console.log("saved object remotely", object);
        } else {
            console.error(atob(response.error_info));
        }
        if (callback) {
            callback();
        }
    });
}

function saveView(currentView: any, done?: (object: any) => void) {
    if (!currentView) {
        currentView = ViewerEvents.view;
    }
    console.log("saveView:", currentView);
    var projectId = currentView.project;
    var selectedView = currentView.ID;
    var selectedViewContainerProperties = ViewerEvents.layers;
    var selectedViewContainerMeta = ViewerEvents.compositeMeta;
    var selectedViewDeleted = ViewerEvents.pendingDeletion;
    var {
        thumbnailPosCaptured,
        selectedViewThumbnailCapture,
        selectedViewCameraOriginCapture,
        selectedViewFocusOriginCapture,
    } = ViewerEvents;

    var remoteUpdateViews = function () {
        var containerString = "";
        Object.keys(selectedViewDeleted).forEach((id: string) => {
            if (selectedViewContainerMeta[id]) {
                delete selectedViewContainerMeta[id];
            }
            if (selectedViewContainerProperties[id]) {
                delete selectedViewContainerProperties[id];
            }
        });

        for (var i in selectedViewContainerProperties) {
            if (i !== selectedView) {
                if (containerString !== "") containerString += " ";
                containerString += i;
            }
        }
        containerString = containerString.trim();
        var view: any = {};
        view["ID"] = selectedView;
        view["project"] = projectId;
        view["name"] = currentView.name;
        view["description"] = currentView.description;
        view["type"] = "FILE";
        view["file_type"] = "VIEW";
        view["view_type"] = "container";
        view["state"] = "COMPLETE";
        view["file_date"] = new Date().getTime() + "";
        view["entity_container"] = containerString;
        var containingFolder = FoldersActions.getContainerFolder(projectId, selectedView);
        view["file_folder"] = containingFolder;
        if (thumbnailPosCaptured && selectedViewCameraOriginCapture.x && selectedViewFocusOriginCapture.x) {
            view["camera_x"] = selectedViewCameraOriginCapture.x + "";
            view["camera_y"] = selectedViewCameraOriginCapture.y + "";
            view["camera_z"] = selectedViewCameraOriginCapture.z + "";
            view["focus_x"] = selectedViewFocusOriginCapture.x + "";
            view["focus_y"] = selectedViewFocusOriginCapture.y + "";
            view["focus_z"] = selectedViewFocusOriginCapture.z + "";
            view["has_thumbnail"] = "1";
        }
        //saving the view
        ViewerEvents.view = view;
        const cachedData = getCachedData();
        updateData("save", "views", selectedView, view);
        updateData("save", "entities", projectId, view);
        addToCurrentProject(view);
        console.log("saved view locally:", view, "|cachedData:", cachedData["views"][selectedView]);
        saveSlides();
        saveObjectRemotely(view, () => {});
        var keys = Object.keys(selectedViewContainerProperties);
        var pending = keys.length;
        keys.forEach((key: string) => {
            var subView = selectedViewContainerProperties[key];
            if (subView.not_selectable) {
                return;
            }
            subView["ID"] = key;
            subView["project"] = projectId;
            subView["type"] = "FILE";
            subView["file_type"] = "VIEW";
            subView["state"] = "COMPLETE";
            subView["file_date"] = new Date().getTime() + "";
            subView["file_folder"] = FoldersActions.getContainerFolder(projectId, key) || "0";
            subView["virtual"] = "1";
            updateData("save", "entities", projectId, subView);
            saveObjectRemotely(subView, () => {
                pending--;
                if (pending === 0) {
                    if (done) {
                        done(selectedView);
                    }
                }
            });
        });
    };

    var pendingElements = getCachedData("pending");
    var arr = Object.values(pendingElements);
    var count = arr.length;
    Object.values(pendingElements).forEach((entity: any, index: number) => {
        if (allowedSaveType(entity.view_type)) {
            if (!entity.action) {
                ApiUtils.remoteUpdateEntity(projectId, entity.ID, entity, (response: any) => {
                    if (response.result === "success") {
                        count--;
                        if (count === 0) {
                            Utils.StorageUtils.saveToSessionStorage("pending", []);
                        }
                        console.log("saved add or update entity:", entity);
                    } else {
                        console.error(atob(response.error_info));
                    }
                });
            } else if (entity.action === "remove") {
                ApiUtils.remoteDeleteItems(projectId, entity.ID, (response: any) => {
                    if (response.result === "success") {
                        count--;
                        if (count === 0) {
                            Utils.StorageUtils.saveToSessionStorage("pending", []);
                        }
                        console.log("saved remove entity:", entity);
                    } else {
                        console.error(atob(response.error_info));
                    }
                });
            }
        }
    });

    if (Object.keys(ViewerEvents.pendingCompositeMeshes).length > 0) {
        Object.keys(ViewerEvents.pendingCompositeMeshes).forEach((layerId: string) => {
            var content = JSON.stringify(Object.values(ViewerEvents.pendingCompositeMeshes[layerId]));
            var file = new File([content], "set_children_properties.txt");
            ApiUtils.postFileToServer(
                currentView.project,
                layerId,
                file,
                0,
                function () {
                    console.log("update file success!");
                },
                function () {
                    console.log("error");
                    // todo: show error
                }
            );
        });
    }

    if (selectedViewThumbnailCapture !== "") {
        // save thumbnail first
        var file = new File([selectedViewThumbnailCapture], "thumbnail.txt");
        ApiUtils.postFileToServer(
            currentView.project,
            selectedView,
            file,
            0,
            function () {
                selectedViewThumbnailCapture = "";
                remoteUpdateViews();
            },
            () => {}
        );
    } else {
        remoteUpdateViews();
    }
}

export function handleClick(event: any, done?: (object: any) => void) {
    const viewer: any = ViewerEvents.viewer;
    if (!viewer) {
        return;
    }
    var selectedView = ViewerEvents.view.ID;
    var projectId = ViewerEvents.view.project;
    var { selectedViewCameraOriginCapture, selectedViewFocusOriginCapture } = ViewerEvents;

    const { cmd, param } = event;
    if (cmd && viewer) {
        switch (cmd) {
            case "save": {
                saveView(param, (object?: any) => {
                    ViewerEvents.emit("onViewSave", {
                        newScene: ViewerEvents.newScene,
                        layers: ViewerEvents.layers,  
                        inputs: ViewerEvents.inputs, 
                        view: ViewerEvents.view
                    });
                    done && done(object);
            });
                break;
            }
            case "addLayers": {
                if (done) {
                    addObjects({
                        items: param.entities,
                        folderId: param.source,
                        done,
                    });
                }
                break;
            }
            case "changeThumbnail": {
                captureViewThumbnail((id: string, iconData: any) => {

                    selectedViewCameraOriginCapture = viewer.getViewer().getCameraOrigin();
                    selectedViewFocusOriginCapture = viewer.getViewer().getFocusOrigin();
                    ViewerEvents.thumbnailPosCaptured = true;
                    ViewerEvents.selectedViewThumbnailCapture = iconData;
                    ViewerEvents.selectedViewCameraOriginCapture = selectedViewCameraOriginCapture;
                    ViewerEvents.selectedViewFocusOriginCapture = selectedViewFocusOriginCapture;
                    updateData("updateIcon", "views", selectedView, iconData);
                    var snapshot = {
                        ID: id,
                        icon: iconData,
                        date: new Date().getTime(),
                        description: "",
                        cameraOrigin: selectedViewCameraOriginCapture,
                        focusOrigin: selectedViewFocusOriginCapture,
                        state: "pending",
                    };
                    var view = getCachedData("views")[selectedView];
                    if (view) {
                        view["camera_x"] = snapshot.cameraOrigin.x + "";
                        view["camera_y"] = snapshot.cameraOrigin.y + "";
                        view["camera_z"] = snapshot.cameraOrigin.z + "";
                        view["focus_x"] = snapshot.focusOrigin.x + "";
                        view["focus_y"] = snapshot.focusOrigin.y + "";
                        view["focus_z"] = snapshot.focusOrigin.z + "";
                        updateData("update", "views", selectedView, view);
                        updateData("updateEntity", "entities", projectId, view);
                        done && done(view);
                    }
                });
                break;
            }
            case "snapshot": {
                captureViewThumbnail((id: string, iconData: any) => {
                    selectedViewCameraOriginCapture = viewer.getViewer().getCameraOrigin();
                    selectedViewFocusOriginCapture = viewer.getViewer().getFocusOrigin();
                    var snapshot = {
                        ID: id,
                        icon: iconData,
                        date: new Date().getTime(),
                        description: "",
                        cameraOrigin: selectedViewCameraOriginCapture,
                        focusOrigin: selectedViewFocusOriginCapture,
                        state: "pending",
                    };
                    updateData("add", "snapshots", selectedView, snapshot);
                    var view = getCachedData("views")[selectedView];
                    if (view) {
                        view["camera_x"] = snapshot.cameraOrigin.x + "";
                        view["camera_y"] = snapshot.cameraOrigin.y + "";
                        view["camera_z"] = snapshot.cameraOrigin.z + "";
                        view["focus_x"] = snapshot.focusOrigin.x + "";
                        view["focus_y"] = snapshot.focusOrigin.y + "";
                        view["focus_z"] = snapshot.focusOrigin.z + "";
                        updateData("update", "views", selectedView, view);
                        updateData("updateEntity", "entities", projectId, view);
                    }
                    if (done) {
                        done(id);
                    }
                });
                break;
            }
            case "updateCrossSectionStepIncrement": {
                const { layerId, aabb, block } = param;
                CrossSectionBounds.setCrossSectionBounds(layerId, aabb, block, false);
                break;
            }
            case "updateCustomCrossSectionStepIncrement": {
                const { block } = param;
                CrossSectionBounds.setCustomCrossSectionBounds(block, false);
                break;
            }
            case "setOrtho": {
                viewer.setOrthographicProjection(param);
                break;
            }
            case "zoomextents": {
                viewer.zoomToExtents();
                break;
            }
            case "zoomregion": {
                const regionData = param;
                viewer.zoomToRegion(regionData);
                break;
            }
            case "fullscreen": {
                viewer.toggleFullscreen();
                break;
            }
            case "zoomin": {
                viewer.applyZoomDelta(param);
                break;
            }
            case "zoomout": {
                viewer.applyZoomDelta(param);
                break;
            }
            case "pan": {
                viewer.setNavigationMode(param);
                break;
            }
            case "vorbit": {
                viewer.setNavigationMode(param);
                break;
            }
            case "updateCrossSectionX": {
                viewer.updateCrossSectionX(param);
                break;
            }
            case "updateCrossSectionY": {
                viewer.updateCrossSectionY(param);
                break;
            }
            case "updateCrossSectionZ": {
                viewer.updateCrossSectionZ(param);
                break;
            }
            case "updateCrossSectionW": {
                viewer.updateCrossSectionCustom(param);
                break;
            }
            case "updateCrossSectionPresetType": {
                //Additionnaly we might want to show/hide custom step increment

                const presetType = param;
                if (presetType === 11) {
                    //Prehaps we still want to keep previous plane until completion
                    viewer.updateCrossSectionPresetType(0);

                    const onComplete = function (data: any) {
                        const worldPointA = data.wPointA;
                        const worldPointB = data.wPointB;
                        const worldPointC = viewer.getCameraWorldPosition();
                        viewer.updateCustomCrossSectionPlaneFromCoplanarPoints(worldPointA, worldPointB, worldPointC);
                        viewer.updateCrossSectionPresetType(11);
                        viewer.deleteSegment("CustomSliceSegmentId");
                    };
                    const onCancel = function (data: any) {
                        //
                    };
                    const onDelete = function (data: any) {
                        //
                    };
                    viewer.startSegment("CustomSliceSegmentId", 0, onComplete, onCancel, onDelete);
                } else {
                    viewer.updateCrossSectionPresetType(presetType);
                    viewer.deleteSegment("CustomSliceSegmentId");
                }
                break;
            }
            case "updateCrossSectionWidth": {
                viewer.updateCrossSectionWidth(param);
                break;
            }
            case "updateCrossSectionBlockModelWidth": {
                viewer.updateCrossSectionBlockModelWidth(param);
                break;
            }
            case "updateCrossSectionDrillHolesWidth": {
                viewer.updateCrossSectionDrillHolesWidth(param);
                break;
            }
            case "updateCrossSectionXDip": {
                viewer.updateCrossSectionXDip(param);
                break;
            }
            case "updateCrossSectionYDip": {
                viewer.updateCrossSectionYDip(param);
                break;
            }
            case "updateCrossSectionZDip": {
                viewer.updateCrossSectionZDip(param);
                break;
            }
            case "updateCrossSectionWDip": {
                viewer.updateCrossSectionCustomDip(param);
                break;
            }
            case "updateCrossSectionXDipAzimuth": {
                viewer.updateCrossSectionXDipAzimuth(param);
                break;
            }
            case "updateCrossSectionYDipAzimuth": {
                viewer.updateCrossSectionYDipAzimuth(param);
                break;
            }
            case "updateCrossSectionZDipAzimuth": {
                viewer.updateCrossSectionZDipAzimuth(param);
                break;
            }
            case "updateCrossSectionWDipAzimuth": {
                viewer.updateCrossSectionCustomDipAzimuth(param);
                break;
            }
            case "setDirection": {
                const directionType = param;
                switch (directionType) {
                    default:
                    case 1: //North
                        viewer.alignCameraToDirection([0.0, 1.0, 0.0]);
                        break;
                    case 2: //South
                        viewer.alignCameraToDirection([0.0, -1.0, 0.0]);
                        break;
                    case 3: //East
                        viewer.alignCameraToDirection([1.0, 0.0, 0.0]);
                        break;
                    case 4: //West
                        viewer.alignCameraToDirection([-1.0, 0.0, 0.0]);
                        break;
                    case 5: //Up
                        viewer.alignCameraToDirection([0.0, 0.0, 1.0]);
                        break;
                    case 6: //Down
                        viewer.alignCameraToDirection([0.0, 0.0, -1.0]);
                        break;
                }
                break;
            }
            case "sliceMode": {
                const presetType = param;
                handleSliceMode(presetType);
                break;
            }
        }
    }
}

export function getLayerSettings(layer: any, attributes: any, defaultValues: any = []) {
    var values: any = {};
    attributes.forEach((attribute: string, index: number) => {
        var name = Utils.slugToCamelCase(attribute);
        if (layer[attribute]) {
            if (typeof defaultValues[index] === "number") {
                values[name] = parseInt(layer[attribute]);
            } else if (typeof defaultValues[index] === "boolean") {
                values[name] = layer[attribute] === "1";
            } else {
                values[name] = layer[attribute];
            }
        } else if (typeof defaultValues[index] !== "undefined") {
            values[name] = defaultValues[index];
        } else {
            values[name] = undefined;
        }
    });
    return values;
}

export function getEntitiesBounds(idsList: any, currentProject: any) {
    var minX, minY, minZ, maxX, maxY, maxZ;
    var box = {
        min: { x: Number.MAX_VALUE, y: Number.MAX_VALUE, z: Number.MAX_VALUE },
        max: { x: -Number.MAX_VALUE, y: -Number.MAX_VALUE, z: -Number.MAX_VALUE },
    };
    idsList.forEach((entityId: string) => {
        let entity: any = currentProject[entityId];
        if (entity && entity["aabb_min_x"]) {
            minX = parseFloat(entity["aabb_min_x"]);
            minY = parseFloat(entity["aabb_min_y"]);
            minZ = parseFloat(entity["aabb_min_z"]);
            maxX = parseFloat(entity["aabb_max_x"]);
            maxY = parseFloat(entity["aabb_max_y"]);
            maxZ = parseFloat(entity["aabb_max_z"]);

            box.min.x = Math.min(minX, box.min.x);
            box.min.y = Math.min(minY, box.min.y);
            box.min.z = Math.min(minZ, box.min.z);

            box.max.x = Math.max(maxX, box.max.x);
            box.max.y = Math.max(maxY, box.max.y);
            box.max.z = Math.max(maxZ, box.max.z);
        }
    });
    return box;
}
